/* © ИП Коробкин И.Н., KIN DIGITAL CRM+ */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { FormEvent, useMemo, useState } from 'react';
import { Button, ErrorText, Field, Input, Modal, PageHeader, Select, Textarea } from '@/components/ui';
import { api, qs } from '@/lib/api';
import { localToIso } from '@/lib/format';

const KIND: Record<string, { label: string; cls: string }> = {
  HEARING: { label: 'Заседание', cls: 'bg-red-100 text-red-800' }, MEETING: { label: 'Встреча', cls: 'bg-brand-100 text-brand-700' },
  DEADLINE: { label: 'Срок', cls: 'bg-amber-100 text-amber-800' }, OTHER: { label: 'Другое', cls: 'bg-slate-100 text-slate-700' },
};
const WD = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'];
const ymd = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

export function CalendarPage() {
  const qc = useQueryClient();
  const [month, setMonth] = useState(() => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1); });
  const [creating, setCreating] = useState<string | null>(null);
  const days = useMemo(() => {
    const start = new Date(month); start.setDate(1 - ((month.getDay() + 6) % 7));
    return Array.from({ length: 42 }, (_, i) => new Date(start.getFullYear(), start.getMonth(), start.getDate() + i));
  }, [month]);
  const from = days[0], to = new Date(days[41].getTime() + 86400_000);
  const { data } = useQuery({ queryKey: ['calendar', ymd(from)], queryFn: () => api.get(`/calendar${qs({ from: from.toISOString(), to: to.toISOString() })}`) });
  const byDay = (d: Date) => {
    const k = ymd(d);
    return [
      ...(data?.events ?? []).filter((e: any) => ymd(new Date(e.startAt)) === k).map((e: any) => ({ ...e, t: 'event' })),
      ...(data?.taskDeadlines ?? []).filter((t: any) => ymd(new Date(t.dueAt)) === k).map((t: any) => ({ ...t, t: 'task' })),
    ];
  };
  const title = month.toLocaleDateString('ru-RU', { month: 'long', year: 'numeric' });
  return (
    <>
      <PageHeader title="Календарь" actions={<>
        <Button variant="secondary" onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() - 1, 1))} aria-label="Назад"><ChevronLeft size={16} /></Button>
        <div className="self-center px-2 text-sm font-medium capitalize">{title}</div>
        <Button variant="secondary" onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() + 1, 1))} aria-label="Вперёд"><ChevronRight size={16} /></Button>
        <Button onClick={() => setCreating(ymd(new Date()))}>Событие</Button>
      </>} />
      <div className="card overflow-hidden">
        <div className="grid grid-cols-7 border-b border-slate-200 bg-slate-50">{WD.map((d) => <div key={d} className="px-2 py-1.5 text-xs font-semibold text-slate-500">{d}</div>)}</div>
        <div className="grid grid-cols-7">
          {days.map((d) => {
            const items = byDay(d);
            const isToday = ymd(d) === ymd(new Date());
            return (
              <div key={d.toISOString()} onDoubleClick={() => setCreating(ymd(d))}
                className={clsx('min-h-[96px] border-b border-r border-slate-100 p-1', d.getMonth() !== month.getMonth() && 'bg-slate-50 text-slate-400')}>
                <div className={clsx('mb-1 inline-block rounded px-1 text-xs', isToday && 'bg-brand-600 text-white')}>{d.getDate()}</div>
                <div className="space-y-0.5">
                  {items.slice(0, 4).map((x: any) => (
                    <div key={x.id} title={x.title} className={clsx('truncate rounded px-1 text-[11px]', x.t === 'task' ? 'bg-slate-100 text-slate-700' : KIND[x.kind]?.cls)}>
                      {x.t === 'task' ? '☐ ' : new Date(x.startAt).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' }) + ' '}{x.title}
                    </div>
                  ))}
                  {items.length > 4 && <div className="text-[11px] text-slate-500">ещё {items.length - 4}</div>}
                </div>
              </div>
            );
          })}
        </div>
      </div>
      <div className="mt-2 text-xs text-slate-500">Двойной щелчок по дню - новое событие. Серым показаны сроки ваших задач.</div>
      <Modal title="Новое событие" open={!!creating} onClose={() => setCreating(null)}>
        {creating && <EventForm day={creating} onSaved={() => { setCreating(null); qc.invalidateQueries({ queryKey: ['calendar'] }); }} />}
      </Modal>
    </>
  );
}

function EventForm({ day, onSaved }: { day: string; onSaved: () => void }) {
  const [f, setF] = useState({ title: '', kind: 'MEETING', start: `${day}T10:00`, end: `${day}T11:00`, location: '', description: '' });
  const save = useMutation({
    mutationFn: () => api.post('/calendar', { title: f.title, kind: f.kind, startAt: localToIso(f.start), endAt: localToIso(f.end), location: f.location || null, description: f.description || null }),
    onSuccess: onSaved,
  });
  const submit = (e: FormEvent) => { e.preventDefault(); save.mutate(); };
  return (
    <form onSubmit={submit} className="space-y-3">
      <Field label="Название"><Input required autoFocus value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} /></Field>
      <Field label="Вид"><Select value={f.kind} onChange={(e) => setF({ ...f, kind: e.target.value })} options={Object.entries(KIND).map(([value, k]) => ({ value, label: k.label }))} /></Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Начало"><Input type="datetime-local" value={f.start} onChange={(e) => setF({ ...f, start: e.target.value })} /></Field>
        <Field label="Окончание"><Input type="datetime-local" value={f.end} onChange={(e) => setF({ ...f, end: e.target.value })} /></Field>
      </div>
      <Field label="Место или ссылка"><Input value={f.location} onChange={(e) => setF({ ...f, location: e.target.value })} /></Field>
      <Field label="Описание"><Textarea value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} /></Field>
      <ErrorText error={save.error} />
      <div className="flex justify-end"><Button type="submit" disabled={save.isPending}>Сохранить</Button></div>
    </form>
  );
}
